<template>
  <main v-if="skills">
    <h2>Skills</h2>

    <form class="filters" @submit.prevent>
      <label>
        Search
        <input v-model="search" type="search" placeholder="name or description" />
      </label>
      <label>
        Weight from (&ge;)
        <input v-model.number="minWeight" type="number" :min="weightRange.min" :max="weightRange.max" />
      </label>
      <label>
        Weight to (&le;)
        <input v-model.number="maxWeight" type="number" :min="weightRange.min" :max="weightRange.max" />
      </label>
      <button type="button" :disabled="!hasFilters" @click="resetFilters">Reset filters</button>
    </form>

    <p class="count">{{ filteredSkills.length }} / {{ skills.length }} skills</p>

    <div v-if="filteredSkills.length" class="skills-grid">
      <RouterLink v-for="skill in filteredSkills" :key="skill.id" :to="{ name: 'skill-detail', params: { id: skill.id } }"
        class="skill-card" :class="{ earned: earnedIds.has(skill.id) }">
        <div class="skill-header" :style="{ backgroundColor: skill.color, color: getTextColor(skill.color) }">
          <span class="skill-name">{{ skill.name }}</span>
          <span class="skill-weight">{{ skill.weight }}</span>
        </div>
        <div class="skill-body">
          <img v-if="skill.icon" :src="skill.icon" alt="" class="skill-icon" />
          <div class="skill-description markdown-content" v-html="markdown2(skill.description || '')"></div>
        </div>
        <span v-if="earnedIds.has(skill.id)" class="earned-badge">Earned</span>
      </RouterLink>
    </div>
    <p v-else class="empty">No skills found.</p>
  </main>
  <main v-else>
    <loading :status="status" />
  </main>
</template>

<style lang="scss" scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  justify-content: center;
  margin: 1rem;

  label {
    display: flex;
    flex-direction: column;
    text-align: left;
    font-size: 0.9rem;
  }

  input {
    padding: 0.4rem 0.6rem;
    font-size: 1rem;
  }

  input[type='number'] {
    width: 8rem;
  }

  button {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 1ex;
    background: $btnColor;
    color: white;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}

.count,
.empty {
  text-align: center;
  margin: 0.5rem;
}

.skills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
  margin: 1rem;
}

.skill-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 2px solid #ccc;
  border-radius: 1ex;
  overflow: hidden;
  color: inherit;
  text-decoration: none;
  text-align: left;

  &:hover {
    border-color: $btnColor;
  }

  &.earned {
    border-color: $primari;
  }

  .skill-header {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0.8rem;
    font-weight: bold;
  }

  .skill-body {
    display: flex;
    gap: 0.8rem;
    align-items: flex-start;
    padding: 0.8rem;
  }

  .skill-icon {
    width: 64px;
    height: 64px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .skill-description {
    max-height: 7.5em;
    overflow: hidden;
    font-size: 0.9rem;
  }

  .earned-badge {
    position: absolute;
    right: 0.5rem;
    bottom: 0.5rem;
    padding: 0.1rem 0.5rem;
    border-radius: 1ex;
    background: $primari;
    color: white;
    font-size: 0.8rem;
  }
}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { api } from '../utils/axios'
import { markdown2, getTextColor } from '../utils/helpers'
import loading from '@/components/loading.vue'

const skills = ref(null)
const earnedIds = ref(new Set())
const status = ref('loading')

const search = ref('')
const minWeight = ref('')
const maxWeight = ref('')

const normalize = (text) =>
  (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const weightRange = computed(() => {
  const weights = (skills.value || []).map((s) => s.weight)
  return { min: Math.min(...weights, 1), max: Math.max(...weights, 10) }
})

const hasFilters = computed(
  () => search.value !== '' || minWeight.value !== '' || maxWeight.value !== ''
)

// v-model.number gives '' for an empty input and null in some cases
const isSet = (value) => value !== '' && value !== null && !Number.isNaN(value)

const filteredSkills = computed(() => {
  const query = normalize(search.value.trim())
  return (skills.value || []).filter((skill) => {
    if (isSet(minWeight.value) && skill.weight < minWeight.value) return false
    if (isSet(maxWeight.value) && skill.weight > maxWeight.value) return false
    if (query) {
      const haystack = normalize(`${skill.name} ${skill.description || ''}`)
      if (!haystack.includes(query)) return false
    }
    return true
  })
})

function resetFilters() {
  search.value = ''
  minWeight.value = ''
  maxWeight.value = ''
}

onMounted(async () => {
  try {
    const [allSkills, userSkills] = await Promise.all([api.get('/skills'), api.get('/user/skills')])
    earnedIds.value = new Set(userSkills.data.skills.map((s) => s.id))
    skills.value = [...allSkills.data].sort((a, b) => a.name.localeCompare(b.name, 'cs'))
  } catch (error) {
    status.value = error.response?.data?.msg || 'Error fetching skills'
    console.error('Error fetching skills:', error)
  }
})
</script>
